import React, { useEffect, useMemo, useState } from 'react';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const parseAgendaData = (props) => {
    if (props.agenda) {
        try {
            return JSON.parse(props.agenda);
        } catch (e) {
            console.warn('Invalid agenda JSON in data-agenda:', e);
        }
    }

    const sourceId = props.agendaSource || 'agenda-data';
    const script = document.getElementById(sourceId);
    if (script && script.textContent) {
        try {
            return JSON.parse(script.textContent);
        } catch (e) {
            console.warn('Invalid agenda JSON in script tag:', e);
        }
    }

    return [];
};

const normalizeEvents = (events) => {
    return (events || [])
        .map((event) => {
            const date = event.date || event.startDate || event.day ||
                         event.data || event.dataInicio || event.data_inicio;
            if (!date) return null;
            return {
                id: event.id || `${event.title || event.titulo || 'event'}-${date}`,
                title: event.title || event.titulo || 'Untitled',
                date,
                time: event.time || event.startTime || event.hora || event.horario || '',
                location: event.location || event.venue || event.local ||
                          event.localização || event.localizacao || event.teatro || '',
                description: event.description || event.descrição || event.descricao || '',
                url: event.url || '',
                showSlug: event.showSlug || event.show || event.showName || event.showTitle ||
                          event.espetaculo || event.espetáculo || event.peca || event.peça ||
                          event.production || ''
            };
        })
        .filter(Boolean)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
};

const getMonthLabel = (date) => date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
const getDayLabel = (iso) => new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
});

const toLocalISODate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const buildCalendarGrid = (currentMonth, events) => {
    const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

    const startOffset = (start.getDay() + 6) % 7; // Monday-first
    const totalCells = Math.ceil((startOffset + end.getDate()) / 7) * 7;

    const days = [];
    for (let i = 0; i < totalCells; i += 1) {
        const day = new Date(start);
        day.setDate(start.getDate() - startOffset + i);
        const iso = toLocalISODate(day);
        const dayEvents = events.filter(ev => ev.date === iso);
        days.push({
            date: day,
            iso,
            isOutside: day.getMonth() !== currentMonth.getMonth(),
            events: dayEvents
        });
    }
    return days;
};

const colorFromString = (value = '') => {
    const palette = [
        '#5B6CFF',
        '#22C55E',
        '#F59E0B',
        '#EF4444',
        '#06B6D4',
        '#A855F7',
        '#F97316'
    ];
    let hash = 0;
    for (let i = 0; i < value.length; i += 1) {
        hash = (hash << 5) - hash + value.charCodeAt(i);
        hash |= 0;
    }
    const index = Math.abs(hash) % palette.length;
    return palette[index];
};

const AgendaPage = (props) => {
    const [view, setView] = useState('calendar');
    const [month, setMonth] = useState(() => new Date());
    const [selectedDate, setSelectedDate] = useState(null);

    const rawData = useMemo(() => parseAgendaData(props), [props]);
    const events = useMemo(() => normalizeEvents(rawData), [rawData]);
    const todayISO = useMemo(() => toLocalISODate(new Date()), []);

    // Cache raw agenda data to sessionStorage so show pages can read it without a fetch
    useEffect(() => {
        if (rawData && rawData.length > 0) {
            try {
                sessionStorage.setItem('jgm-agenda-data', JSON.stringify(rawData));
            } catch (e) {
                // sessionStorage unavailable — silently ignore
            }
        }
    }, [rawData]);

    const calendarDays = useMemo(() => buildCalendarGrid(month, events), [month, events]);
    const selectedEvents = useMemo(
        () => (selectedDate ? events.filter((event) => event.date === selectedDate) : []),
        [events, selectedDate]
    );

    const goPrev = () => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
    const goNext = () => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));

    useEffect(() => {
        if (!selectedDate && events.length > 0) {
            const todayEvent = events.find((event) => event.date === todayISO);
            setSelectedDate(todayEvent ? todayEvent.date : events[0].date);
        }
    }, [events, selectedDate, todayISO]);

    return (
        <section className="agenda-page">
            <style>{`
                .agenda-page {
                    --agenda-bg: #f7f8fc;
                    --agenda-surface: #ffffff;
                    --agenda-surface-soft: #f1f5ff;
                    --agenda-text: #0f172a;
                    --agenda-muted: #475569;
                    --agenda-border: #dbe3ef;
                    --agenda-accent: #375dfb;
                    --agenda-accent-2: #7c3aed;
                    --agenda-past: #dc2626;
                    --agenda-current: #16a34a;
                    --agenda-upcoming: #eab308;
                    --agenda-today-ring: rgba(55, 93, 251, 0.32);
                    --agenda-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
                    max-width: var(--max-width);
                    margin: 0 auto;
                    padding: var(--space-2xl) var(--space-xl);
                    background: radial-gradient(circle at 8% -20%, rgba(124, 58, 237, 0.08), transparent 45%),
                                radial-gradient(circle at 100% 0%, rgba(55, 93, 251, 0.12), transparent 40%),
                                var(--agenda-bg);
                    color: var(--agenda-text);
                    border-radius: 22px;
                }

                [data-theme="dark"] .agenda-page {
                    --agenda-bg: #020817;
                    --agenda-surface: #0b1220;
                    --agenda-surface-soft: #0f172a;
                    --agenda-text: #f8fafc;
                    --agenda-muted: #cbd5e1;
                    --agenda-border: #23324d;
                    --agenda-accent: #60a5fa;
                    --agenda-accent-2: #a78bfa;
                    --agenda-past: #f87171;
                    --agenda-current: #4ade80;
                    --agenda-upcoming: #facc15;
                    --agenda-today-ring: rgba(96, 165, 250, 0.4);
                    --agenda-shadow: 0 14px 36px rgba(0, 0, 0, 0.45);
                }

                .agenda-header {
                    display: flex;
                    flex-wrap: wrap;
                    gap: var(--space-lg);
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: var(--space-xl);
                }

                .agenda-title {
                    font-size: clamp(1.75rem, 2.5vw, 2.5rem);
                    font-weight: 800;
                    margin: 0 0 0.25rem 0;
                }

                .agenda-subtitle {
                    color: var(--agenda-muted);
                    margin: 0;
                }

                .agenda-controls {
                    display: flex;
                    gap: var(--space-sm);
                    align-items: center;
                    flex-wrap: wrap;
                }

                .agenda-month {
                    font-weight: 700;
                    padding: 0 6px;
                    color: var(--agenda-text);
                }

                .agenda-btn {
                    padding: 10px 14px;
                    border-radius: 10px;
                    border: 1px solid var(--agenda-border);
                    background: var(--agenda-surface);
                    color: var(--agenda-text);
                    cursor: pointer;
                    font-weight: 600;
                    transition: all var(--transition-fast);
                }

                .agenda-btn:hover {
                    background: var(--agenda-surface-soft);
                    border-color: color-mix(in srgb, var(--agenda-accent) 40%, var(--agenda-border));
                }

                .agenda-btn.is-active {
                    background: linear-gradient(135deg, var(--agenda-accent), var(--agenda-accent-2));
                    color: #ffffff;
                    border-color: transparent;
                }

                .agenda-calendar {
                    display: grid;
                    grid-template-columns: repeat(7, minmax(0, 1fr));
                    gap: 10px;
                }

                .agenda-weekday {
                    text-transform: uppercase;
                    font-size: 0.75rem;
                    letter-spacing: 0.08em;
                    color: var(--agenda-muted);
                    padding: 8px 6px;
                    font-weight: 700;
                }

                .agenda-day {
                    border: 1px solid var(--agenda-border);
                    border-radius: 14px;
                    padding: 10px;
                    min-height: 120px;
                    background: var(--agenda-surface);
                    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
                    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
                    position: relative;
                }

                .agenda-day:hover {
                    border-color: color-mix(in srgb, var(--agenda-accent) 45%, var(--agenda-border));
                    box-shadow: var(--agenda-shadow);
                    transform: translateY(-1px);
                }

                .agenda-day.is-outside {
                    opacity: 0.5;
                    background: color-mix(in srgb, var(--agenda-surface) 70%, transparent);
                }

                .agenda-day.is-today {
                    border-color: color-mix(in srgb, var(--agenda-accent) 65%, var(--agenda-border));
                    box-shadow: 0 0 0 3px var(--agenda-today-ring), var(--agenda-shadow);
                }

                .agenda-day.is-today::before {
                    content: 'Today';
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    font-size: 0.65rem;
                    font-weight: 700;
                    letter-spacing: 0.04em;
                    color: #ffffff;
                    background: linear-gradient(135deg, var(--agenda-accent), var(--agenda-accent-2));
                    border-radius: 999px;
                    padding: 0.2rem 0.45rem;
                    z-index: 1;
                }

                .agenda-day-number {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 2.35rem;
                    height: 2.35rem;
                    padding: 0 0.7rem;
                    border-radius: 999px;
                    font-weight: 800;
                    margin-bottom: 8px;
                    color: var(--agenda-text);
                    background: rgba(148, 163, 184, 0.22);
                    -webkit-text-fill-color: currentColor;
                }

                .agenda-day-button {
                    width: 100%;
                    text-align: left;
                    display: flex;
                    align-items: flex-start;
                    justify-content: flex-start;
                    background: transparent !important;
                    border: none !important;
                    box-shadow: none !important;
                    padding: 0 !important;
                    color: var(--agenda-text) !important;
                    -webkit-text-fill-color: var(--agenda-text) !important;
                    font: inherit;
                    cursor: pointer;
                    border-radius: 8px;
                    appearance: none;
                }

                .agenda-day-button:disabled {
                    cursor: default;
                    opacity: 1 !important;
                    color: var(--agenda-text) !important;
                    -webkit-text-fill-color: var(--agenda-text) !important;
                    background: transparent !important;
                }

                .agenda-day-button:focus-visible {
                    outline: 2px solid var(--agenda-accent);
                    outline-offset: 2px;
                    border-radius: 10px;
                }

                .agenda-day.has-events .agenda-day-button {
                    background: transparent !important;
                    padding: 0 !important;
                }

                .agenda-day.is-selected .agenda-day-button {
                    box-shadow: none !important;
                }

                .agenda-day.has-events .agenda-day-number {
                    background: color-mix(in srgb, var(--day-accent, var(--agenda-accent)) 18%, white);
                    border: 1px solid color-mix(in srgb, var(--day-accent, var(--agenda-accent)) 40%, transparent);
                }

                .agenda-day.has-events.is-past-show .agenda-day-number,
                .agenda-day.has-events.is-current-show .agenda-day-number,
                .agenda-day.has-events.is-upcoming-show .agenda-day-number {
                    color: #ffffff !important;
                    -webkit-text-fill-color: #ffffff !important;
                    border: none;
                }

                .agenda-day.has-events.is-past-show .agenda-day-number {
                    background: var(--agenda-past);
                }

                .agenda-day.has-events.is-current-show .agenda-day-number {
                    background: var(--agenda-current);
                }

                .agenda-day.has-events.is-upcoming-show .agenda-day-number {
                    background: var(--agenda-upcoming);
                }

                .agenda-day.is-selected:not(.has-events) .agenda-day-number,
                .agenda-day.is-today .agenda-day-number {
                    background: linear-gradient(135deg, var(--agenda-accent), var(--agenda-accent-2));
                    color: #ffffff !important;
                    -webkit-text-fill-color: #ffffff !important;
                    border: none;
                }

                .agenda-day-events {
                    margin-top: 8px;
                }

                .agenda-event {
                    padding: 6px 8px;
                    border-radius: 8px;
                    background: color-mix(in srgb, var(--day-accent, var(--agenda-accent)) 14%, transparent);
                    border: 1px solid color-mix(in srgb, var(--day-accent, var(--agenda-accent)) 36%, var(--agenda-border));
                    margin-bottom: 6px;
                    font-size: 0.85rem;
                    line-height: 1.2;
                    color: var(--agenda-text);
                }

                .agenda-event-title {
                    font-weight: 600;
                }

                .agenda-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: var(--space-lg);
                }

                .agenda-table th,
                .agenda-table td {
                    padding: 12px;
                    border-bottom: 1px solid var(--theme-border);
                    text-align: left;
                    vertical-align: top;
                }

                .agenda-table th {
                    text-transform: uppercase;
                    font-size: 0.75rem;
                    letter-spacing: 0.08em;
                    color: var(--agenda-muted);
                }

                .agenda-empty {
                    padding: var(--space-xl);
                    border: 1px dashed var(--agenda-border);
                    border-radius: 12px;
                    text-align: center;
                    color: var(--agenda-muted);
                    background: color-mix(in srgb, var(--agenda-surface) 88%, transparent);
                }

                @media (max-width: 900px) {
                    .agenda-page {
                        padding: var(--space-xl) var(--space-md);
                    }

                    .agenda-calendar {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }

                    .agenda-weekday {
                        display: none;
                    }
                }

                @media (max-width: 700px) {
                    .agenda-header {
                        gap: var(--space-md);
                    }

                    .agenda-controls {
                        width: 100%;
                        justify-content: flex-start;
                    }

                    .agenda-btn {
                        flex: 1 1 auto;
                        min-width: 120px;
                    }

                    .agenda-calendar {
                        grid-template-columns: repeat(7, minmax(0, 1fr));
                        gap: 6px;
                    }

                    .agenda-day {
                        min-height: auto;
                        padding: 6px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .agenda-day-number {
                        margin: 0;
                    }

                    .agenda-day-button {
                        width: auto;
                        height: auto;
                        border-radius: 999px;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: 700;
                    }

                    .agenda-day-events {
                        display: none;
                    }

                    .agenda-mobile-details {
                        margin-top: var(--space-lg);
                        padding: var(--space-md);
                        border: 1px solid var(--agenda-border);
                        border-radius: 12px;
                        background: var(--agenda-surface);
                    }

                    .agenda-mobile-details-title {
                        font-weight: 700;
                        margin-bottom: var(--space-sm);
                    }

                    .agenda-mobile-event {
                        padding: 10px 12px;
                        border-radius: 10px;
                        border: 1px solid var(--agenda-border);
                        background: var(--agenda-surface-soft);
                        margin-bottom: 10px;
                    }

                    .agenda-mobile-event:last-child {
                        margin-bottom: 0;
                    }

                    .agenda-mobile-event-title {
                        font-weight: 700;
                        margin-bottom: 4px;
                    }

                    .agenda-mobile-event-meta {
                        color: var(--agenda-muted);
                        font-size: 0.9rem;
                    }

                    .agenda-table thead {
                        display: none;
                    }

                    .agenda-table,
                    .agenda-table tbody,
                    .agenda-table tr,
                    .agenda-table td {
                        display: block;
                        width: 100%;
                    }

                    .agenda-table tr {
                        border: 1px solid var(--agenda-border);
                        border-radius: 12px;
                        padding: var(--space-md);
                        margin-bottom: var(--space-md);
                        background: var(--agenda-surface);
                    }

                    .agenda-table td {
                        padding: 8px 0;
                        border: none;
                        display: flex;
                        justify-content: space-between;
                        gap: var(--space-md);
                    }

                    .agenda-table td::before {
                        content: attr(data-label);
                        font-weight: 600;
                        color: var(--agenda-muted);
                    }
                }
            `}</style>
            <div className="agenda-header">
                <div>
                    <h1 className="agenda-title" data-translate-key="Agenda">Agenda</h1>
                    <p className="agenda-subtitle" data-translate-key="agenda.subtitle">Calendar and list of upcoming shows.</p>
                </div>
                <div className="agenda-controls">
                    <button className="agenda-btn" onClick={goPrev} type="button" data-translate-key="agenda.prev">Prev</button>
                    <div className="agenda-month">{getMonthLabel(month)}</div>
                    <button className="agenda-btn" onClick={goNext} type="button" data-translate-key="agenda.next">Next</button>
                    <button
                        className={`agenda-btn ${view === 'calendar' ? 'is-active' : ''}`}
                        onClick={() => setView('calendar')}
                        type="button"
                        data-translate-key="agenda.view_calendar"
                    >
                        Calendar
                    </button>
                    <button
                        className={`agenda-btn ${view === 'list' ? 'is-active' : ''}`}
                        onClick={() => setView('list')}
                        type="button"
                        data-translate-key="agenda.view_list"
                    >
                        List
                    </button>
                </div>
            </div>

            {events.length === 0 && (
                <div className="agenda-empty" data-translate-key="agenda.empty">No events yet. Add agenda data to the page content.</div>
            )}

            {events.length > 0 && view === 'calendar' && (
                <div>
                    <div className="agenda-calendar">
                        {WEEKDAYS.map((day) => (
                            <div key={day} className="agenda-weekday">{day}</div>
                        ))}
                        {calendarDays.map((day) => {
                            const isToday = day.iso === todayISO;
                            const isSelected = selectedDate === day.iso;
                            const hasEvents = day.events.length > 0;
                            const accent = hasEvents ? colorFromString(day.events[0].title) : 'var(--agenda-accent)';
                            const showStatus = hasEvents
                                ? day.iso < todayISO
                                    ? 'is-past-show'
                                    : day.iso === todayISO
                                        ? 'is-current-show'
                                        : 'is-upcoming-show'
                                : '';

                            return (
                            <div
                                key={day.iso}
                                className={`agenda-day ${day.isOutside ? 'is-outside' : ''} ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''} ${hasEvents ? 'has-events' : ''} ${showStatus}`}
                                style={{ '--day-accent': accent }}
                            >
                                <button
                                    type="button"
                                    className="agenda-day-button"
                                    onClick={() => day.events.length > 0 && setSelectedDate(day.iso)}
                                    disabled={day.events.length === 0}
                                    aria-pressed={selectedDate === day.iso}
                                >
                                    <span className="agenda-day-number">{day.date.getDate()}</span>
                                </button>
                                <div className="agenda-day-events">
                                    {day.events.map((event) => (
                                        <div
                                            key={event.id}
                                            className="agenda-event"
                                            style={{ '--day-accent': colorFromString(event.title) }}
                                        >
                                            <div className="agenda-event-title">{event.title}</div>
                                            {event.time && <div>{event.time}</div>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            );
                        })}
                    </div>
                    <div className="agenda-mobile-details" aria-live="polite">
                        {selectedDate && selectedEvents.length > 0 ? (
                            <>
                                <div className="agenda-mobile-details-title">{getDayLabel(selectedDate)}</div>
                                {selectedEvents.map((event) => (
                                    <div key={event.id} className="agenda-mobile-event">
                                        <div className="agenda-mobile-event-title">{event.title}</div>
                                        <div className="agenda-mobile-event-meta">
                                            {event.time ? `${event.time}` : (
                                                <span data-translate-key="agenda.time_tbc">Time TBC</span>
                                            )}
                                            {event.location ? ` • ${event.location}` : ''}
                                        </div>
                                        {event.description && <div className="agenda-mobile-event-meta">{event.description}</div>}
                                    </div>
                                ))}
                            </>
                        ) : (
                            <div className="agenda-mobile-event-meta" data-translate-key="agenda.tap_day">Tap a day with a show to see details.</div>
                        )}
                    </div>
                </div>
            )}

            {events.length > 0 && view === 'list' && (
                <table className="agenda-table">
                    <thead>
                        <tr>
                            <th data-translate-key="agenda.table_date">Date</th>
                            <th data-translate-key="agenda.table_time">Time</th>
                            <th data-translate-key="agenda.table_show">Show</th>
                            <th data-translate-key="agenda.table_location">Location</th>
                        </tr>
                    </thead>
                    <tbody>
                        {events.map((event) => (
                            <tr key={event.id}>
                                <td data-label="Date">{event.date}</td>
                                <td data-label="Time">{event.time || '-'}</td>
                                <td data-label="Show">
                                    {event.url ? (
                                        <a href={event.url}>{event.title}</a>
                                    ) : (
                                        event.title
                                    )}
                                </td>
                                <td data-label="Location">{event.location || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
};

export default AgendaPage;
